import { DialogStyle } from "@tableau/extensions-api-types";

import {
  ensureTableauApiLoaded,
  type TableauApiLoadOptions,
} from "./tableau-api-loader";

export interface InitializeOptions {
  /**
   * Dynamic loading options for the Tableau Extensions API library.
   */
  tableauApi?: TableauApiLoadOptions;
  /**
   * Configuration options for the Tableau extension.
   * Adding the config property will enable the extension to be configurable
   */
  config?: {
    dialogOptions?: {
      width?: number;
      height?: number;
      dialogStyle: "modal" | "modeless" | "window";
    };
    /**
     * Route to the configuration page. Use an relative path (e.g. /config)
     */
    route: string;
  };
}

function getDefaultDialogOptions() {
  return {
    height: 500,
    width: 500,
    dialogStyle: tableau.DialogStyle.Modal as "modal" | "modeless" | "window",
  };
}

export class TableauManager {
  constructor(readonly options?: InitializeOptions) {}

  public static async initialize(options?: InitializeOptions) {
    await ensureTableauApiLoaded(options?.tableauApi);

    return await tableau.extensions.initializeAsync({
      ...(options?.config && {
        configure: () => this.configure(options),
      }),
    });
  }

  private static configure = async (options?: InitializeOptions) => {
    const popupUrl = `${window.location.origin}${options?.config?.route}`;

    const dialogOptions =
      options?.config?.dialogOptions ?? getDefaultDialogOptions();

    function mapDisplayStyleStringLiteralToEnum(
      displayStyleString: "modal" | "modeless" | "window",
    ): DialogStyle {
      switch (displayStyleString) {
        case "modal":
          return tableau.DialogStyle.Modal;
        case "modeless":
          return tableau.DialogStyle.Modeless;
        case "window":
          return tableau.DialogStyle.Window;
      }
    }

    try {
      await tableau.extensions.ui.displayDialogAsync(popupUrl, undefined, {
        ...dialogOptions,
        dialogStyle: mapDisplayStyleStringLiteralToEnum(
          dialogOptions.dialogStyle,
        ),
      });
    } catch (error) {
      if (error instanceof Error && "errorCode" in error) {
        switch (error.errorCode) {
          case tableau.ErrorCodes.DialogClosedByUser:
            break;
          default:
            console.error(error);
        }
      } else {
        console.error(error);
      }
    }
  };
}
